const FEISHU_JSAPI_URL = 'https://lf-scm-cn.feishucdn.com/lark/op/h5-js-sdk-1.5.44.js';
const FEISHU_ORG_ID_KEY = 'feishu_org_id';
const FEISHU_JSAPI_SCRIPT_ID = 'aha-feishu-jsapi-script';
const FEISHU_JSAPI_TIMEOUT_MS = 20000;

let feishuJsapiLoadPromise: Promise<void> | null = null;

export function isFeishuEnv(): boolean {
  return /Lark|lark/i.test(navigator.userAgent);
}

export function saveFeishuOrgId(orgId: string): void {
  try {
    sessionStorage.setItem(FEISHU_ORG_ID_KEY, orgId);
  } catch (_) {}
}

export function getStoredFeishuOrgId(): string | null {
  try {
    return sessionStorage.getItem(FEISHU_ORG_ID_KEY);
  } catch (_) {
    return null;
  }
}

export function isFeishuJsapiLoading(): boolean {
  return !window.h5sdk && !!feishuJsapiLoadPromise;
}

export function loadFeishuJsapi(): Promise<void> {
  if (window.h5sdk) {
    return Promise.resolve();
  }
  if (feishuJsapiLoadPromise) {
    return feishuJsapiLoadPromise;
  }

  feishuJsapiLoadPromise = new Promise<void>((resolve, reject) => {
    let timeoutId: number | null = null;
    const clearAll = () => {
      if (timeoutId !== null) {
        window.clearTimeout(timeoutId);
      }
    };
    const onLoad = () => {
      clearAll();
      if (window.h5sdk) {
        resolve();
      } else {
        reject(new Error('Feishu JSAPI script loaded but h5sdk unavailable'));
      }
    };
    const onError = () => {
      clearAll();
      reject(new Error('Failed to load Feishu JSAPI SDK'));
    };

    timeoutId = window.setTimeout(() => {
      reject(new Error('Feishu JSAPI SDK load timeout'));
    }, FEISHU_JSAPI_TIMEOUT_MS);

    let script = document.getElementById(FEISHU_JSAPI_SCRIPT_ID) as HTMLScriptElement | null;
    if (!script) {
      script = document.createElement('script');
      script.id = FEISHU_JSAPI_SCRIPT_ID;
      script.src = FEISHU_JSAPI_URL;
      script.async = true;
      document.head.appendChild(script);
    }
    script.addEventListener('load', onLoad, { once: true });
    script.addEventListener('error', onError, { once: true });
  })
    .catch((error) => {
      feishuJsapiLoadPromise = null;
      throw error;
    });

  return feishuJsapiLoadPromise;
}

export function preloadFeishuJsapiInBackground(): void {
  if (!isFeishuEnv()) return;
  loadFeishuJsapi().catch((error) => {
    console.warn('[Feishu] preload jsapi failed:', error);
  });
}

export function getFeishuAuthCode(appId: string, scopeList?: string[]): Promise<string> {
  return new Promise((resolve, reject) => {
    if (!window.h5sdk) {
      reject(new Error('Feishu h5sdk not loaded'));
      return;
    }
    window.h5sdk.ready(() => {
      if (typeof tt === 'undefined') {
        reject(new Error('Feishu tt API not available'));
        return;
      }
      tt.requestAccess({
        scopeList: scopeList || ['contact:user.email:readonly', 'directory:employee.base.enterprise_email:read'],
        appID: appId,
        success: (res) => resolve(res.code),
        fail: (err) => reject(new Error(`requestAccess failed: ${err.errMsg}`)),
      });
    });
    window.h5sdk.error((err) => {
      reject(new Error(`h5sdk error: ${JSON.stringify(err)}`));
    });
  });
}
